import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import endOfWeek from 'date-fns/endOfWeek'
import startOfWeek from 'date-fns/startOfWeek'

import { FormProvider, useForm } from 'react-hook-form'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom-v5-compat'
import { useFlags } from 'launchdarkly-react-client-sdk'
import { Box, Button, IconButton, useMediaQuery, useTheme } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import ChevronLeft from '@mui/icons-material/ChevronLeft'
import ChevronRight from '@mui/icons-material/ChevronRight'
import MoreVert from '@mui/icons-material/MoreVert'

import { get } from 'utils/lodash'
import { handleError } from 'utils/error'
import { formatDateToBEFormatDateFns } from 'utils/date'
import { useLazyGetAllConfiguredServicesQuery } from 'api/configured-service/searchConfiguredServices'
import { canAddService, canUpdateService } from 'data/permissions/permissionsSelectors'
import { CACHE_TAG_CONFIGURED_SERVICES_LIST } from 'api/cacheTagTypes'
import { selectConfiguredServicesFilters } from 'slices/customer/customerDetailsSlice'
import { getGlobalFilterOptions } from 'middleware/actions/globalsearch'
import { getConfiguredServiceDetailsPageUrl, getCustomerDetailsPageUrl } from 'router/routes'
import { isCSDetailsAvailableFeaturesAvailable } from 'data/launch-darkly/featureSelectors'

import T from 'T'
import api from 'api'
import HHAccordion from 'components/common/HHAccordion'
import EventsDrawer from 'components/customer-details/drawer/events'
import { useLazyGetDraftConfiguredServiceByAccountIdQuery } from 'api/configured-service/draftPricingCSCrud'
import { CreateDraftConfiguredServiceDialog } from 'components/configured-service'
import {
  openCSD,
  closeCSD,
  selectIsCSDOpen,
  selectCSDDrawerWidth,
  selectCSDIsFilterPanelOpen,
  selectCSDCurrentTab,
  CREATE_DRAFT_CS_TABS,
  setDrawerWidth,
  setIsFilterPanelOpen,
  FILTER_PANEL_COLLAPSE_WIDTH,
  FILTER_PANEL_DEFAULT_OPEN_WIDTH,
} from 'slices/configuredService/createDraftCSDSlice'
import { selectIsLocationsPanelOpen, setIsLocationsPanelOpen } from 'slices/customer/persistentCustomerDetailsServicesSlice'
import { Z_INDEX } from 'theme/zIndex'
import HeaderActionMenu from './actions/HeaderActionMenu'
import ConfiguredServiceList from './ConfiguredServiceList'
import ConfiguredServiceDatePickerSection from './common/ConfiguredServiceDatePickerSection'

const MIN_DRAWER_WIDTH = 400
const MAX_DRAWER_WIDTH = 1400

const ConfiguredServicesV2 = ({ accountId, locationId = '', expandWindowIfNeeded, ensureDateInWindow, isCalendarDataLoading = false }) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const theme = useTheme()
  const isTabletOrMobile = useMediaQuery(theme.breakpoints.down('md'))
  const flags = useFlags()
  const isCSAvailableFeatureAvailable = isCSDetailsAvailableFeaturesAvailable({ flags })
  const configuredServiceForm = useForm({
    defaultValues: {
      startDate: startOfWeek(new Date()),
      endDate: endOfWeek(new Date()),
    },
  })
  const { watch } = configuredServiceForm
  const startDate = watch('startDate')
  const endDate = watch('endDate')
  const addService = useSelector(canAddService, shallowEqual)
  const updateService = useSelector(canUpdateService, shallowEqual)
  const [headerActionMenuAnchorEl, setHeaderActionMenuAnchorEl] = useState(null)
  const [selectedService, setSelectedService] = useState(null)
  const [isOpenEventsDrawer, setIsOpenEventsDrawer] = useState(false)
  const dragCleanupRef = useRef(null)
  const previousLocationsPanelOpenRef = useRef(true)
  const isLocationsPanelOpen = useSelector(selectIsLocationsPanelOpen, shallowEqual)
  const isOpenPricingCSDialog = useSelector(selectIsCSDOpen, shallowEqual)
  const drawerWidth = useSelector(selectCSDDrawerWidth, shallowEqual)
  const isFilterPanelOpen = useSelector(selectCSDIsFilterPanelOpen, shallowEqual)
  const currentTab = useSelector(selectCSDCurrentTab, shallowEqual)
  const isScheduleTab = currentTab === CREATE_DRAFT_CS_TABS.SCHEDULE
  const selectedServiceId = get(selectedService, 'configuredServiceId')
  const [getDraftConfiguredServices, { data: draftServices = [], isLoading: isGetDraftCSLoading }] =
    useLazyGetDraftConfiguredServiceByAccountIdQuery()
  const [getAllConfiguredServices, { data: publishedServices = [], isFetching, isLoading }] = useLazyGetAllConfiguredServicesQuery()

  const csFilter = useSelector(selectConfiguredServicesFilters, shallowEqual)
  const withDeactivated = get(csFilter, 'withDeactivated')

  const handleAddNewDraftService = useCallback(
    event => {
      event.stopPropagation()
      setSelectedService(null)
      previousLocationsPanelOpenRef.current = isLocationsPanelOpen
      dispatch(setIsLocationsPanelOpen(false))
      const newDrawerWidth = isTabletOrMobile ? window.innerWidth : Math.min(MAX_DRAWER_WIDTH, Math.max(900, window.innerWidth - 200))
      dispatch(openCSD({ drawerWidth: newDrawerWidth }))
    },
    [dispatch, isTabletOrMobile, isLocationsPanelOpen]
  )

  const handleOpenHeaderActionMenu = useCallback(event => {
    event.stopPropagation()
    setHeaderActionMenuAnchorEl(event.currentTarget)
  }, [])

  const handleCloseHeaderActionMenu = useCallback(() => setHeaderActionMenuAnchorEl(null), [])

  const getServiceDetailsUrl = useCallback(
    service => {
      const isDraft = get(service, 'isDraft', false)
      const serviceId = isDraft ? service.id : service.configuredServiceId
      const baseUrl = getConfiguredServiceDetailsPageUrl(serviceId)
      const params = new URLSearchParams({ locationId, accountId }).toString()
      return `${baseUrl}?${params}`
    },
    [locationId, accountId]
  )

  const handleNavigateToEditService = useCallback(
    (event, service) => {
      event.stopPropagation()
      const baseUrl = getServiceDetailsUrl(service)
      const separator = baseUrl.includes('?') ? '&' : '?'
      navigate(`${baseUrl}${separator}openAdjust=true`)
    },
    [navigate, getServiceDetailsUrl]
  )

  const handleAddEventClick = useCallback((event, service) => {
    event.stopPropagation()
    setSelectedService(service)
    setIsOpenEventsDrawer(true)
  }, [])

  const handleRefetchServices = useCallback(() => {
    dispatch(api.util.invalidateTags([CACHE_TAG_CONFIGURED_SERVICES_LIST]))
  }, [dispatch])

  const handleDraftCreationSuccess = useCallback(
    (service, tab) => {
      const baseUrl = getConfiguredServiceDetailsPageUrl(service?.id || service?.configuredServiceId)
      const params = new URLSearchParams({ locationId, accountId })
      if (tab) {
        params.append('tab', tab)
      }
      const url = `${baseUrl}?${params.toString()}`
      return navigate(url)
    },
    [locationId, accountId, navigate]
  )

  const handleDraftCreationSuccessReturnToAccount = useCallback(() => {
    navigate(`${getCustomerDetailsPageUrl(accountId)}?tab=services`)
  }, [accountId, navigate])

  const handleCloseCreateEditPricingCSDialog = useCallback(() => {
    dispatch(setIsLocationsPanelOpen(previousLocationsPanelOpenRef.current))
    dispatch(closeCSD())
  }, [dispatch])

  const handleResizeMouseDown = useCallback(
    e => {
      e.preventDefault()
      const startX = e.clientX
      const startWidth = drawerWidth
      document.body.style.cursor = 'ew-resize'

      const handleMouseMove = moveEvent => {
        const delta = startX - moveEvent.clientX
        const minWidth = isFilterPanelOpen ? FILTER_PANEL_COLLAPSE_WIDTH : MIN_DRAWER_WIDTH
        const newWidth = Math.max(minWidth, Math.min(MAX_DRAWER_WIDTH, startWidth + delta))
        if (newWidth < FILTER_PANEL_COLLAPSE_WIDTH && isFilterPanelOpen) {
          dispatch(setIsFilterPanelOpen(false))
        }
        dispatch(setDrawerWidth(newWidth))
      }

      const cleanup = () => {
        document.body.style.cursor = ''
        document.removeEventListener('mousemove', handleMouseMove)
        document.removeEventListener('mouseup', handleMouseUp)
        dragCleanupRef.current = null
      }

      const handleMouseUp = () => cleanup()

      dragCleanupRef.current = cleanup
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
    },
    [drawerWidth, isFilterPanelOpen, dispatch]
  )

  const handleToggleFilterPanel = useCallback(() => {
    const opening = !isFilterPanelOpen
    if (opening && drawerWidth < FILTER_PANEL_DEFAULT_OPEN_WIDTH) {
      dispatch(setDrawerWidth(FILTER_PANEL_DEFAULT_OPEN_WIDTH))
    }
    dispatch(setIsFilterPanelOpen(opening))
  }, [dispatch, isFilterPanelOpen, drawerWidth])

  const allServices = useMemo(() => {
    return [...publishedServices.map(s => ({ ...s, isDraft: false })), ...draftServices.map(s => ({ ...s, isDraft: true }))]
  }, [publishedServices, draftServices])

  useEffect(() => {
    if (accountId || locationId) {
      getAllConfiguredServices({
        accountId,
        locationId,
        startDate: formatDateToBEFormatDateFns(startDate),
        endDate: formatDateToBEFormatDateFns(endDate),
      })
        .unwrap()
        .catch(handleError)
    }
  }, [accountId, locationId, startDate, endDate, getAllConfiguredServices])

  useEffect(() => {
    if (locationId && isCSAvailableFeatureAvailable) {
      getDraftConfiguredServices({
        accountId,
      })
    }
  }, [accountId, locationId, isCSAvailableFeatureAvailable, getDraftConfiguredServices])

  useEffect(() => {
    dispatch(getGlobalFilterOptions())
  }, [dispatch])

  useEffect(() => {
    if (!isOpenPricingCSDialog || isTabletOrMobile) return

    let resizeTimer
    const handleWindowResize = () => {
      clearTimeout(resizeTimer)
      resizeTimer = setTimeout(() => {
        if (drawerWidth > window.innerWidth) {
          dispatch(setDrawerWidth(window.innerWidth))
        }
      }, 150)
    }

    window.addEventListener('resize', handleWindowResize)
    return () => {
      clearTimeout(resizeTimer)
      window.removeEventListener('resize', handleWindowResize)
    }
  }, [isOpenPricingCSDialog, isTabletOrMobile, drawerWidth, dispatch])

  useEffect(() => {
    return () => {
      if (dragCleanupRef.current) {
        dragCleanupRef.current()
      }
    }
  }, [])

  return (
    <>
      <HHAccordion
        withNewColors
        isExpanded
        isExpandCollapseHeightSame
        summary={T.SERVICES}
        showActionIcon={false}
        showCustomActions
        customActionContent={
          <Box display="flex" alignItems="center">
            {addService && (
              <IconButton onClick={handleAddNewDraftService}>
                <AddIcon />
              </IconButton>
            )}
            <IconButton onClick={handleOpenHeaderActionMenu}>
              <MoreVert />
            </IconButton>
          </Box>
        }
      >
        <FormProvider {...configuredServiceForm}>
          <ConfiguredServiceDatePickerSection />
          <Box width="100%" mt={2}>
            <ConfiguredServiceList
              isFetching={isFetching}
              isLoading={isLoading || isGetDraftCSLoading}
              selectedServiceId={selectedServiceId}
              services={allServices}
              withDeactivated={withDeactivated}
              canEdit={updateService}
              onEditService={handleNavigateToEditService}
              showAddEventButton
              onAddEventClick={handleAddEventClick}
              locationId={locationId}
              accountId={accountId}
            />
          </Box>
        </FormProvider>
      </HHAccordion>
      {updateService && selectedServiceId && (
        <EventsDrawer
          isOpenDrawer={isOpenEventsDrawer}
          configuredServiceId={selectedServiceId}
          expandWindowIfNeeded={expandWindowIfNeeded}
          ensureDateInWindow={ensureDateInWindow}
          isCalendarDataLoading={isCalendarDataLoading}
          onRefetch={handleRefetchServices}
          onClose={() => {
            setIsOpenEventsDrawer(false)
            setSelectedService(null)
          }}
        />
      )}
      <HeaderActionMenu anchorEl={headerActionMenuAnchorEl} onClose={handleCloseHeaderActionMenu} />
      {isOpenPricingCSDialog && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            right: 0,
            height: '100vh',
            width: isScheduleTab || isTabletOrMobile ? '100vw' : drawerWidth,
            zIndex: Z_INDEX.NAVIGATION,
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '-4px 0 16px rgba(0,0,0,0.15)',
          }}
        >
          {/* Filter panel toggle tongue — hidden on schedule tab and mobile/tablet */}
          {!isScheduleTab && !isTabletOrMobile && (
            <Button
              variant="text"
              onClick={handleToggleFilterPanel}
              sx={{
                position: 'absolute',
                left: -16,
                top: '50%',
                transform: 'translateY(-50%)',
                minWidth: 16,
                width: 16,
                height: 48,
                p: 0,
                bgcolor: 'primary.main',
                color: 'common.white',
                borderTopRightRadius: 0,
                borderBottomRightRadius: 0,
                borderTopLeftRadius: 10,
                borderBottomLeftRadius: 10,
                zIndex: 1,
                '&:hover': { bgcolor: 'primary.dark' },
              }}
            >
              {isFilterPanelOpen ? <ChevronRight /> : <ChevronLeft />}
            </Button>
          )}
          {/* Resize handle — hidden on schedule tab and mobile/tablet */}
          {!isScheduleTab && !isTabletOrMobile && (
            <Box
              onMouseDown={handleResizeMouseDown}
              sx={{
                position: 'absolute',
                left: 0,
                top: 0,
                bottom: 0,
                width: 4,
                cursor: 'ew-resize',
                zIndex: 1,
                '&:hover': { bgcolor: 'primary.main', opacity: 0.5 },
              }}
            />
          )}
          <CreateDraftConfiguredServiceDialog
            isOpen={isOpenPricingCSDialog}
            accountId={accountId}
            locationId={locationId}
            onClose={handleCloseCreateEditPricingCSDialog}
            onSuccess={handleDraftCreationSuccess}
            onSuccessReturnToAccount={handleDraftCreationSuccessReturnToAccount}
            renderAsPanel
          />
        </Box>
      )}
    </>
  )
}

ConfiguredServicesV2.propTypes = {
  locationId: PropTypes.string,
  accountId: PropTypes.string.isRequired,
  expandWindowIfNeeded: PropTypes.func,
  ensureDateInWindow: PropTypes.func,
  isCalendarDataLoading: PropTypes.bool,
}

export default ConfiguredServicesV2
